// src/components/Destinations.tsx
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import PriceTag from '../components/PriceTag';

const Section = styled.section`
    padding: 80px 0;
`;

const SectionTitle = styled(motion.h2)`
    font-size: 3rem;
    font-weight: 800;
    text-align: center;
    margin-bottom: 1rem;
    color: #1a1a1a;
    line-height: 1.2;

    @media (max-width: 768px) {
        font-size: 2.5rem;
    }
`;

const SectionSubtitle = styled(motion.p)`
    font-size: 1.25rem;
    text-align: center;
    margin-bottom: 4rem;
    color: #666;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
`;

const DestinationsSection = styled(Section)`
    background: #f8fafc;
`;

const DestinationsGrid = styled.div`
    display: grid;
    /* Cap card width so a single tour doesn't stretch across the whole section */
    grid-template-columns: repeat(auto-fit, minmax(350px, 560px));
    justify-content: center;
    gap: 2rem;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 1.5rem;
    }
`;

const DestinationCard = styled(motion.div)`
    background: white;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
    border: 1px solid #e5e7eb;
    transition: all 0.3s ease;
    position: relative;

    &:hover {
        transform: translateY(-8px);
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
    }
`;

const DestinationImage = styled.div<{ bgImage: string }>`
    height: 250px;
    background:
            linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1)),
            url(${props => props.bgImage}) center/cover;
    position: relative;
`;

const DestinationBadge = styled.div<{ type: string }>`
    position: absolute;
    top: 1rem;
    left: 1rem;
    background: ${props => {
        switch (props.type) {
            case 'Most Popular': return 'linear-gradient(135deg, #667eea, #764ba2)';
            case 'Cultural': return 'linear-gradient(135deg, #10b981, #059669)';
            case 'Adventure': return 'linear-gradient(135deg, #f59e0b, #d97706)';
            default: return 'linear-gradient(135deg, #667eea, #764ba2)';
        }
    }};
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.875rem;
    font-weight: 600;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const DestinationContent = styled.div`
    padding: 1.5rem;
`;

const DestinationTitle = styled.h3`
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    color: #1a1a1a;
    line-height: 1.3;
`;

const DestinationSubtitle = styled.p`
    color: #667eea;
    font-weight: 600;
    margin-bottom: 0.75rem;
    font-size: 1rem;
`;

const DestinationDescription = styled.p`
    color: #666;
    line-height: 1.5;
    margin-bottom: 1.5rem;
    font-size: 0.95rem;
`;

const DestinationFeatures = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;
    background: #f8fafc;
    padding: 1rem;
    border-radius: 10px;
`;

const FeatureItem = styled.div`
    text-align: center;
`;

const FeatureLabel = styled.div`
    color: #667eea;
    font-weight: 600;
    font-size: 0.8rem;
    margin-bottom: 0.25rem;
`;

const FeatureValue = styled.div`
    color: #1a1a1a;
    font-weight: 600;
    font-size: 0.9rem;
`;

const DestinationButton = styled(motion.button)`
    width: 100%;
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    border: none;
    padding: 0.875rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 0.95rem;
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3);

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
    }
`;

interface DestinationsProps {
    onNavigateToTrips: () => void;
}

const Destinations: React.FC<DestinationsProps> = ({ onNavigateToTrips }) => {
    const destinations = [
        {
            id: 1,
            title: "From Bratislava: Castles, Čičmany & Bratislava Castle",
            subtitle: "Historic Castles & Traditional Village",
            description: "Discover Slovakia's rich history on a tour from Bratislava. Visit Červený Kameň, Smolenice and Trenčín Castles, explore the village of Čičmany, Slovak Bethlehem and end at the Bratislava Castle.",
            originalPrice: "169€",
            price: "119€",
            duration: "10 hours",
            groupSize: "2-7 people",
            image: "https://upload.wikimedia.org/wikipedia/commons/b/b4/Bratislava_-_Burg_%28b%29.JPG",
            badge: "Cultural",
            type: "Cultural" as const
        }
    ];

    return (
        <DestinationsSection>
            <SectionTitle
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
            >
                Featured Tours
            </SectionTitle>
            <SectionSubtitle
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
            >
                Discover the best of the heart Europe with our carefully curated experiences. From imperial cities to alpine adventures, each tour is designed to create unforgettable memories.
            </SectionSubtitle>
            <DestinationsGrid>
                {destinations.map((destination, index) => (
                    <DestinationCard
                        key={destination.id}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.2 }}
                        viewport={{ once: true }}
                        whileHover={{ y: -8 }}
                    >
                        <DestinationImage bgImage={destination.image}>
                            <DestinationBadge type={destination.type}>{destination.badge}</DestinationBadge>
                        </DestinationImage>
                        <DestinationContent>
                            <DestinationTitle>{destination.title}</DestinationTitle>
                            <DestinationSubtitle>{destination.subtitle}</DestinationSubtitle>
                            <DestinationDescription>{destination.description}</DestinationDescription>

                            <DestinationFeatures>
                                <FeatureItem>
                                    <FeatureLabel>DURATION</FeatureLabel>
                                    <FeatureValue>{destination.duration}</FeatureValue>
                                </FeatureItem>
                                <FeatureItem>
                                    <FeatureLabel>GROUP SIZE</FeatureLabel>
                                    <FeatureValue>{destination.groupSize}</FeatureValue>
                                </FeatureItem>
                            </DestinationFeatures>

                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                                <PriceTag
                                    prefix="From "
                                    originalPrice={destination.originalPrice}
                                    price={destination.price}
                                    size="md"
                                    align="center"
                                />
                            </div>
                            <DestinationButton
                                onClick={onNavigateToTrips}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                View Details & Book
                            </DestinationButton>
                        </DestinationContent>
                    </DestinationCard>
                ))}
            </DestinationsGrid>
        </DestinationsSection>
    );
};

export default Destinations;
